import * as React from 'react';

import { Activity } from './interfaces';
import { TARGET_GRADE_RANGE_FILTERS, TARGET_GRADE_RANGE_BANDS } from './shared';

import { Checkbox, DISABLED, Tooltip, helpIcon, } from '../../../../../Shared/index';

interface TargetGradeRangeFilterRowProps {
  band: string,
  targetGradeRangeFilters: string[],
  handleTargetGradeRangeFilterChange: (targetGradeRangeFilters: string[]) => void,
  filteredActivities: Activity[],
}

interface TargetGradeRangeFiltersProps {
  filterActivities: (ignoredKey?: string) => Activity[],
  targetGradeRangeFilters: string[],
  handleTargetGradeRangeFilterChange: (targetGradeRangeFilters: string[]) => void,
}

const tooltipText = `<p>The target grade range reflects the grade band each activity is designed for based on text complexity, rigor, and aligned standards.</p><br /><p>Filter by one or more bands to find activities designed for the grades you teach. Students never see the grade range of the activities they're assigned.</p><br /><p>Click the “?” icon to learn more about how we determine grade ranges.</p>`

const TargetGradeRangeFilterRow = ({ band, targetGradeRangeFilters, handleTargetGradeRangeFilterChange, filteredActivities, }: TargetGradeRangeFilterRowProps) => {
  const activityCount = filteredActivities.filter(act => (act.grade_band_names || []).includes(band)).length
  const selected = targetGradeRangeFilters.includes(band)
  const disabled = activityCount === 0 && !selected

  function toggleCheckbox() {
    if (disabled) { return }
    if (selected) {
      handleTargetGradeRangeFilterChange(targetGradeRangeFilters.filter(b => b !== band))
    } else {
      handleTargetGradeRangeFilterChange(Array.from(new Set(targetGradeRangeFilters.concat([band]))))
    }
  }

  return (
    <div className="individual-row filter-row">
      <div>
        <Checkbox
          ariaLabel={`Filter by ${band} target grade range`}
          onClick={toggleCheckbox}
          selected={selected}
          state={disabled ? DISABLED : null}
        />
        <span>{band}</span>
      </div>
      <span>({activityCount})</span>
    </div>
  )
}

const TargetGradeRangeFilters = ({ filterActivities, targetGradeRangeFilters, handleTargetGradeRangeFilterChange, }: TargetGradeRangeFiltersProps) => {
  const allBandsSelected = TARGET_GRADE_RANGE_BANDS.every(band => targetGradeRangeFilters.includes(band))

  function toggleSelectAll() {
    handleTargetGradeRangeFilterChange(allBandsSelected ? [] : [...TARGET_GRADE_RANGE_BANDS])
  }

  const filteredActivities = filterActivities(TARGET_GRADE_RANGE_FILTERS)

  const bandRows = TARGET_GRADE_RANGE_BANDS.map(band => (
    <TargetGradeRangeFilterRow
      band={band}
      filteredActivities={filteredActivities}
      handleTargetGradeRangeFilterChange={handleTargetGradeRangeFilterChange}
      key={band}
      targetGradeRangeFilters={targetGradeRangeFilters}
    />
  ))

  const tooltipTriggerElement = (
    <a aria-label="Learn more about how Quill determines grade ranges (opens in a new tab)" className="focus-on-light" href="https://support.quill.org/en/articles/6445559-how-does-quill-determine-the-grade-level-range-for-activities" rel="noopener noreferrer" target="_blank">
      <img alt="" src={helpIcon.src} />
    </a>
  )

  return (
    <section aria-labelledby="targetGradeRangeTitle" className="filter-section target-grade-range-filter-section">
      <div className="name-and-clear-wrapper">
        <h2 id="targetGradeRangeTitle">
          <span>Target Grade Range</span>
          <Tooltip
            isTabbable={false}
            tooltipText={tooltipText}
            tooltipTriggerText={tooltipTriggerElement}
          />
        </h2>
      </div>
      <button className="interactive-wrapper select-all focus-on-light" onClick={toggleSelectAll} type="button">{allBandsSelected ? 'Unselect all' : 'Select all'}</button>
      {bandRows}
    </section>
  )
}

export default TargetGradeRangeFilters
